import {Component, Inject, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {
  FacilityDobryAgentLabels, OpeningHoursDaysOrder,
  OpeningHoursVerificationItem,
  VerificationData,
  VerificationFacility,
  VerificationImages
} from '../../interface/facility-dobry-agent';
import {environment} from '../../../../environments/environment';

export interface FacilityDobryAgentVerificationEditDialogData {
  verification: VerificationFacility;
}

type EditableFieldType =
  | 'text'
  | 'textarea'
  | 'editor'
  | 'boolean'
  | 'openHours'
  | 'daysOffFromWork'
  | 'subCategories'
  | 'paymentMethods'
  | 'insuranceCompanies'
  | 'images';

interface EditableVerificationField {
  key: string;
  label: string;
  type: EditableFieldType;
}

@Component({
  selector: 'app-facility-dobry-agent-verification-edit-dialog',
  templateUrl: './facility-dobry-agent-verification-edit-dialog.component.html',
  styleUrls: ['./facility-dobry-agent-verification-edit-dialog.component.scss'],
})
export class FacilityDobryAgentVerificationEditDialogComponent implements OnInit {
  form: FormGroup = this.fb.group({});
  processingRequest = false;
  dobryAgentBaseUrl = environment.dobryAgentUrl;

  fields: EditableVerificationField[] = [];

  readonly openingHoursDays = OpeningHoursDaysOrder

  dayOffDateControl = new FormControl(null);
  dayOffDateFromControl = new FormControl(null);
  dayOffDateToControl = new FormControl(null);

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis placówki...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      [],
    ],
  };

  private readonly labels = FacilityDobryAgentLabels

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FacilityDobryAgentVerificationEditDialogData,
    private readonly dialogRef: MatDialogRef<FacilityDobryAgentVerificationEditDialogComponent>,
    private readonly fb: FormBuilder,
    private readonly snackBar: MatSnackBar,
  ) {
  }

  ngOnInit(): void {
    this.buildFormFromChanges();
  }

  get verification(): VerificationFacility {
    return this.data.verification;
  }

  get daysOffFromWorkArray(): FormArray {
    return this.form.get('daysOffFromWork') as FormArray;
  }

  private buildFormFromChanges(): void {
    const changes = this.verification?.changes ?? {};
    const keys = Object.keys(changes);

    this.fields = keys.map((key) => ({
      key,
      label: this.getFieldLabel(key),
      type: this.resolveFieldType(key),
    }));

    this.fields.forEach((field) => {
      const value = this.getChangedValue(field.key);

      switch (field.type) {
        case 'openHours':
          this.form.addControl(field.key, this.buildOpenHoursGroup(value));
          break;

        case 'daysOffFromWork':
          this.form.addControl(field.key, this.buildDaysOffArray(value));
          break;

        case 'subCategories':
        case 'paymentMethods':
        case 'insuranceCompanies':
          this.form.addControl(field.key, this.fb.control(this.mapToUuidArray(value)));
          break;

        case 'images':
          this.form.addControl(field.key, this.fb.control(value ?? []));
          break;

        case 'boolean':
          this.form.addControl(field.key, this.fb.control(!!value));
          break;

        default:
          this.form.addControl(field.key, this.fb.control(value ?? ''));
          break;
      }

      if (field.key === 'email') {
        this.form.get(field.key)?.setValidators([Validators.email]);
        this.form.get(field.key)?.updateValueAndValidity();
      }
    });
  }

  private resolveFieldType(field: string): EditableFieldType {
    if (field === 'description') {
      return 'editor';
    }

    if (field === 'parking' || field === 'accessibilityFeatures' || field === 'bestAgents') {
      return 'boolean';
    }

    if (field === 'openHours') {
      return 'openHours';
    }

    if (field === 'daysOffFromWork') {
      return 'daysOffFromWork';
    }

    if (field === 'subCategories') {
      return 'subCategories';
    }

    if (field === 'paymentMethods') {
      return 'paymentMethods';
    }

    if (field === 'insuranceCompanies') {
      return 'insuranceCompanies';
    }

    if (field === 'images') {
      return 'images';
    }

    if (field === 'seoDescription' || field === 'seoKeywords') {
      return 'textarea';
    }

    return 'text';
  }

  private buildOpenHoursGroup(value: any): FormGroup {
    const group = this.fb.group({});

    this.openingHoursDays.forEach((day) => {
      const item = value?.[day.key] ?? null;

      const openTime = this.normalizeTime(item?.openTime);
      const closeTime = this.normalizeTime(item?.closeTime);

      const closed =
        item?.openTime?.toLowerCase?.() === 'nieczynne'
        || item?.closeTime?.toLowerCase?.() === 'nieczynne'
        || (!openTime && !closeTime);

      group.addControl(day.key, this.fb.group({
        openTime: [{value: closed ? '' : openTime, disabled: closed}],
        closeTime: [{value: closed ? '' : closeTime, disabled: closed}],
        closed: [closed],
      }));
    });

    return group;
  }

  private buildDaysOffArray(value: unknown): FormArray {
    const array = this.fb.array([]);

    if (!Array.isArray(value)) {
      return array;
    }

    value
      .filter(Boolean)
      .sort()
      .forEach((date) => {
        array.push(this.fb.control(date));
      });

    return array;
  }

  getOpeningHoursDayGroup(dayKey: string): FormGroup {
    return this.form.get(['openHours', dayKey]) as FormGroup;
  }

  onClosedChange(dayKey: string): void {
    const group = this.getOpeningHoursDayGroup(dayKey);

    const openTimeControl = group.get('openTime');
    const closeTimeControl = group.get('closeTime');

    if (group.get('closed')?.value) {
      openTimeControl?.setValue('');
      closeTimeControl?.setValue('');

      openTimeControl?.disable({emitEvent: false});
      closeTimeControl?.disable({emitEvent: false});
      return;
    }

    openTimeControl?.enable({emitEvent: false});
    closeTimeControl?.enable({emitEvent: false});
  }

  addSingleDayOff(): void {
    const date = this.normalizeDatepickerValue(this.dayOffDateControl.value);

    if (!date) {
      this.snackBar.open('Wybierz datę nieobecności', '', {
        duration: 2000,
      });

      return;
    }

    this.addDayOffDate(this.formatDateForPayload(date));
    this.dayOffDateControl.setValue(null);
  }

  addDayOffRange(): void {
    const from = this.normalizeDatepickerValue(this.dayOffDateFromControl.value);
    const to = this.normalizeDatepickerValue(this.dayOffDateToControl.value);

    if (!from || !to) {
      this.snackBar.open('Wybierz datę od i datę do', '', {
        duration: 2000,
      });

      return;
    }

    if (from.getTime() > to.getTime()) {
      this.snackBar.open('Data od nie może być późniejsza niż data do', '', {
        duration: 2500,
      });

      return;
    }

    const current = new Date(from);

    while (current.getTime() <= to.getTime()) {
      this.addDayOffDate(this.formatDateForPayload(current));
      current.setDate(current.getDate() + 1);
    }

    this.dayOffDateFromControl.setValue(null);
    this.dayOffDateToControl.setValue(null);
  }

  removeDayOff(index: number): void {
    this.daysOffFromWorkArray.removeAt(index);
  }

  private addDayOffDate(date: string): void {
    const exists = this.daysOffFromWorkArray.controls.some((control) => control.value === date);

    if (exists) {
      return;
    }

    this.daysOffFromWorkArray.push(this.fb.control(date));
    this.sortDaysOffFromWork();
  }

  private sortDaysOffFromWork(): void {
    const sortedDates = this.daysOffFromWorkArray.value
      .filter((date: string) => !!date)
      .sort();

    this.daysOffFromWorkArray.clear();

    sortedDates.forEach((date: string) => {
      this.daysOffFromWorkArray.push(this.fb.control(date));
    });
  }

  saveAndAccept(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      this.snackBar.open('Popraw błędy w formularzu', '', {
        duration: 2000,
      });

      return;
    }

    this.processingRequest = true;

    const payload = this.preparePayload();

    this.processingRequest = false;

    this.dialogRef.close({
      saved: true,
      verification: this.verification,
      payload,
    });
  }

  private preparePayload(): any {
    const rawValue = this.form.getRawValue();
    const payload: any = {};

    Object.keys(rawValue).forEach((field) => {
      if (field === 'openHours') {
        payload[field] = this.prepareOpeningHoursPayload(rawValue[field]);
        return;
      }

      if (field === 'daysOffFromWork') {
        payload[field] = (rawValue[field] ?? []).filter(Boolean).sort();
        return;
      }

      if (field === 'website' || field === 'facebook') {
        payload[field] = this.normalizeUrl(rawValue[field]);
        return;
      }

      payload[field] = rawValue[field];
    });

    return payload;
  }

  private prepareOpeningHoursPayload(openHours: any): any {
    const payload: any = {};

    this.openingHoursDays.forEach((day) => {
      const item = openHours[day.key];

      payload[day.key] = {
        openTime: item.closed ? 'nieczynne' : item.openTime,
        closeTime: item.closed ? 'nieczynne' : item.closeTime,
      };
    });

    return payload;
  }

  cancel(): void {
    this.dialogRef.close({
      saved: false,
    });
  }

  getPreviousValue(field: string): unknown {
    return this.verification.previous?.[field as keyof VerificationData] ?? null;
  }

  getChangedValue(field: string): unknown {
    return this.verification.changes?.[field as keyof VerificationData] ?? null;
  }

  getFieldLabel(field: string): string {
    return this.labels[field] || field;
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'boolean') {
      return value ? 'Tak' : 'Nie';
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '—';
      }

      return value
        .map((item) => {
          if (typeof item === 'string' || typeof item === 'number') {
            return String(item);
          }

          if (item?.name) {
            return String(item.name);
          }

          if (item?.location) {
            return String(item.location);
          }

          return JSON.stringify(item);
        })
        .join(', ');
    }

    if (typeof value === 'object') {
      const objectValue = value as any;

      if (objectValue.name) {
        return String(objectValue.name);
      }

      if (objectValue.uuid) {
        return String(objectValue.uuid);
      }

      return JSON.stringify(value);
    }

    return String(value);
  }

  formatOpeningHours(value: unknown): OpeningHoursVerificationItem[] {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return [];
    }

    const openHours = value as Record<string, { openTime?: string; closeTime?: string }>;

    return this.openingHoursDays
      .filter((day) => !!openHours[day.key])
      .map((day) => {
        const openTime = openHours[day.key]?.openTime || '';
        const closeTime = openHours[day.key]?.closeTime || '';

        const closed =
          openTime.toLowerCase() === 'nieczynne'
          || closeTime.toLowerCase() === 'nieczynne'
          || !openTime
          || !closeTime;

        return {
          key: day.key,
          label: day.label,
          openTime,
          closeTime,
          closed,
        };
      });
  }

  asImages(value: unknown): VerificationImages[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is VerificationImages => {
      return typeof item === 'object'
        && item !== null
        && 'location' in item;
    });
  }

  getImageUrl(location?: string | null): string {
    if (!location) {
      return '';
    }

    if (location.startsWith('http://') || location.startsWith('https://')) {
      return location;
    }

    return environment.dobryAgentUrl + `${location}`;
  }

  private mapToUuidArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item: any) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }

        return item?.uuid ?? item?.id ?? null;
      })
      .filter(Boolean);
  }

  private normalizeTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    if (value.toLowerCase() === 'nieczynne') {
      return '';
    }

    const parts = value.split(':');

    if (parts.length < 2) {
      return '';
    }

    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  private normalizeUrl(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return '';
    }

    if (/^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    return `https://${trimmedValue}`;
  }

  private normalizeDatepickerValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value?.toDate === 'function') {
      return this.clearDateTime(value.toDate());
    }

    if (value instanceof Date) {
      return this.clearDateTime(value);
    }

    if (typeof value === 'string') {
      const [year, month, day] = value.split('-').map(Number);

      if (!year || !month || !day) {
        return null;
      }

      return new Date(year, month - 1, day);
    }

    return null;
  }

  private clearDateTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private formatDateForPayload(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
